import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import TaskDrawer from "@/components/TaskDrawer";
import ColumnPicker from "@/components/ColumnPicker";
import { TaskReportPreview } from "@/components/TaskReportPreview";
import { useColumnFilters, HeaderFilter } from "@/components/ColumnFilter";
import { Plus, Search, LayoutGrid, List, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export const STATUSES = ["not_started", "in_progress", "on_hold", "completed"];
export const STATUS_LABEL = {
  not_started: "Not Started",
  in_progress: "In Progress",
  on_hold: "On Hold",
  completed: "Completed",
  delayed: "Delayed",
};
export const TASK_TYPES = ["Admin", "Data Gathering", "HMRC Contact", "Accounts Submission", "Self-Assessment Submission", "Payroll Submission", "VAT Submission"];

export const SERVICE_TYPES = [
  "Accounts",
  "Payroll",
  "VAT",
  "Self-Assessment",
  "Pension",
  "Company Formation",
  "Confirmation Statement",
  "Other Business Support",
];
export const TASK_BILLING = [
  { key: "part_of_existing", label: "Part of Existing Billing" },
  { key: "goodwill", label: "Unpaid — Gesture of Goodwill" },
  { key: "additional", label: "Additional Billing Required" },
];
export const TASK_BILLING_LABEL = Object.fromEntries(TASK_BILLING.map((b) => [b.key, b.label]));

const KANBAN_COLS = ["not_started", "in_progress", "on_hold", "completed"];
const TABS = ["all", "not_started", "in_progress", "on_hold", "completed", "delayed"];
const TAB_LABEL = { all: "All", ...STATUS_LABEL };

const TASK_COLUMNS = [
  { key: "client", label: "Client" },
  { key: "service_type", label: "Service Type" },
  { key: "task_type", label: "Task Type" },
  { key: "task_billing", label: "Billing" },
  { key: "assigned_by", label: "Assigned By" },
  { key: "assignee", label: "Assign To" },
  { key: "start_date", label: "Start Date" },
  { key: "deadline", label: "Deadline" },
  { key: "completion_date", label: "Completion" },
  { key: "priority", label: "Priority" },
  { key: "status", label: "Status" },
];

export default function Tasks() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({
    tab: "all",
    q: "",
    mine_only: false,
    include_archived: false,
  });
  const [view, setView] = useState("table");
  const [activeId, setActiveId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [visibleCols, setVisibleCols] = useState(new Set(TASK_COLUMNS.map((c) => c.key)));

  async function load() {
    // Fetch ALL tasks — filtering happens client-side so KPI card counts stay accurate
    const params = filters.include_archived ? { include_archived: true } : {};
    const [t, c, u] = await Promise.all([
      api.get("/tasks", { params }),
      api.get("/clients"),
      api.get("/users"),
    ]);
    setTasks(t.data); setClients(c.data); setUsers(u.data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.include_archived]);

  const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c.client_name])), [clients]);
  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u.name])), [users]);

  // Scope tasks by "mine only" and search/dropdowns first — status tabs count from THAT scope
  const scopedTasks = useMemo(() => {
    return tasks.filter((t) => {
      if (filters.mine_only && user?.id && t.assignee_id !== user.id) return false;
      if (filters.q) {
        const q = filters.q.toLowerCase();
        const hay = `${t.name || ""} ${t.description || ""} ${clientMap[t.client_id] || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tasks, filters, user, clientMap]);

  const cols = useMemo(() => {
    const d = (k) => ({ date: true, value: (t) => (t[k] || "").slice(0, 10), sort: (t) => (t[k] || "").slice(0, 10) });
    return {
      name: { value: (t) => t.name },
      client: { value: (t) => clientMap[t.client_id] || (t.all_clients ? "All Clients" : "") },
      service_type: { value: (t) => t.service_type },
      task_type: { value: (t) => t.task_type },
      task_billing: { value: (t) => TASK_BILLING_LABEL[t.task_billing] },
      assigned_by: { value: (t) => userMap[t.assigned_by_id] },
      assignee: { value: (t) => userMap[t.assignee_id] },
      start_date: d("start_date"), deadline: d("deadline"), completion_date: d("completion_date"),
      priority: { value: (t) => (t.priority ? t.priority[0].toUpperCase() + t.priority.slice(1) : ""), sort: (t) => ["urgent", "high", "medium", "low"].indexOf(t.priority) },
      status: { value: (t) => STATUS_LABEL[t.status] || t.status },
    };
  }, [clientMap, userMap]);
  const ctl = useColumnFilters(scopedTasks, cols, null);

  const counts = useMemo(() => {
    const c = { all: ctl.view.length, not_started: 0, in_progress: 0, on_hold: 0, completed: 0, delayed: 0 };
    ctl.view.forEach((t) => { c[t.status] = (c[t.status] || 0) + 1; });
    return c;
  }, [ctl.view]);

  const visibleTasks = useMemo(() => {
    if (filters.tab === "all") return ctl.view;
    return ctl.view.filter((t) => t.status === filters.tab);
  }, [ctl.view, filters.tab]);
  const H = (key, label) => <th><HeaderFilter label={label} colKey={key} rows={scopedTasks} cols={cols} ctl={ctl} testid={`hdr-${key}`} /></th>;

  return (
    <div className="p-8 md:p-12">
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="overline mb-2">{filters.mine_only ? "My tasks" : "All tasks"} · {counts.all}</div>
          <h1 className="font-display text-4xl">Tasks.</h1>
        </div>
        <div className="flex gap-2 items-center">
          <ColumnPicker columns={TASK_COLUMNS} storageKey="finchley.tasks.cols.v1" onChange={setVisibleCols} testid="task-cols" />
          <button data-testid="view-table" onClick={() => setView("table")} className={`btn-ghost p-2 ${view === "table" ? "border-[var(--ink)]" : ""}`}><List size={16} strokeWidth={1.5} /></button>
          <button data-testid="view-kanban" onClick={() => setView("kanban")} className={`btn-ghost p-2 ${view === "kanban" ? "border-[var(--ink)]" : ""}`}><LayoutGrid size={16} strokeWidth={1.5} /></button>
          {isAdmin && <TaskReportPreview />}
          <button data-testid="new-task-btn" onClick={() => setShowNew(true)} className="btn-primary flex items-center gap-2"><Plus size={14} strokeWidth={1.5} /> New task</button>
        </div>
      </div>

      {/* Tabs — status counts live here to avoid duplication */}
      <div className="border-b border-[var(--line)] flex gap-6 mb-4 overflow-x-auto" data-testid="task-tabs">
        {TABS.map((tab) => {
          const dotClass = tab === "all" ? "" :
            tab === "not_started" ? "bg-[var(--ink-2)]" :
            tab === "in_progress" ? "bg-[var(--navy)]" :
            tab === "on_hold" ? "bg-[var(--gold)]" :
            tab === "completed" ? "bg-[var(--ok)]" :
            "bg-[var(--err)]";
          const active = filters.tab === tab;
          return (
            <button
              key={tab}
              onClick={() => setFilters((f) => ({ ...f, tab }))}
              data-testid={`tab-${tab}`}
              className={`pb-3 text-sm whitespace-nowrap border-b-2 -mb-px transition flex items-center gap-2 ${active ? "border-[var(--ink)] text-[var(--ink)] font-medium" : "border-transparent text-[var(--ink-2)] hover:text-[var(--ink)]"}`}
            >
              {dotClass && <span className={`inline-block w-1.5 h-1.5 rounded-full ${dotClass}`} />}
              {TAB_LABEL[tab]}
              <span className="font-mono-data text-[11px] text-[var(--ink-2)]">{counts[tab] || 0}</span>
            </button>
          );
        })}
      </div>

      {/* Slim filter bar — all controls inline */}
      <div className="flex gap-2 flex-wrap items-center mb-6 text-sm" data-testid="task-filter-bar">
        <div className="relative">
          <Search size={13} strokeWidth={1.5} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--ink-2)]" />
          <input
            data-testid="task-search"
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            placeholder="Search…"
            className="input-slim pl-8 w-56"
          />
        </div>
        <label className="flex items-center gap-1.5 cursor-pointer select-none px-2.5 py-1.5 rounded border border-[var(--line)] bg-[var(--surface)] hover:border-[var(--ink)] transition" data-testid="filter-mine-only-label">
          <input
            type="checkbox"
            data-testid="filter-mine-only"
            checked={filters.mine_only}
            onChange={(e) => setFilters((f) => ({ ...f, mine_only: e.target.checked }))}
            className="accent-[var(--navy)] w-3.5 h-3.5"
          />
          <span className="text-xs">My tasks only</span>
        </label>
        {isAdmin && (
          <label className="flex items-center gap-1.5 cursor-pointer select-none px-2.5 py-1.5 rounded border border-[var(--line)] bg-[var(--surface)] hover:border-[var(--ink)] transition" data-testid="filter-archived-label" title="Include archived tasks">
            <input
              type="checkbox"
              data-testid="filter-include-archived"
              checked={filters.include_archived}
              onChange={(e) => setFilters((f) => ({ ...f, include_archived: e.target.checked }))}
              className="accent-[var(--navy)] w-3.5 h-3.5"
            />
            <span className="text-xs">Show archived</span>
          </label>
        )}
        {(filters.q || filters.mine_only || ctl.anyActive) && (
          <button
            data-testid="filter-clear"
            onClick={() => { setFilters((f) => ({ ...f, q: "", mine_only: false })); ctl.clearAll(); }}
            className="text-xs text-[var(--ink-2)] underline hover:text-[var(--ink)] ml-1"
          >
            Clear
          </button>
        )}
      </div>

      {isAdmin && selected.size > 0 && (() => {
        const selectedArchivedIds = tasks.filter((t) => selected.has(t.id) && t.archived).map((t) => t.id);
        const selectedArchivedCount = selectedArchivedIds.length;
        return (
        <div className="card-flat p-3 mb-3 flex items-center justify-between border-l-4 border-l-[var(--gold)] flex-wrap gap-2" data-testid="bulk-action-bar">
          <div className="text-sm">
            <span className="font-medium">{selected.size}</span> task{selected.size === 1 ? "" : "s"} selected
            {selectedArchivedCount > 0 && (
              <span className="text-[var(--ink-2)] text-xs ml-2">({selectedArchivedCount} archived)</span>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              data-testid="bulk-archive-btn"
              disabled={busy}
              onClick={async () => {
                if (!window.confirm(`Archive ${selected.size} selected task${selected.size === 1 ? "" : "s"}?`)) return;
                setBusy(true);
                const ids = Array.from(selected);
                const results = await Promise.allSettled(ids.map((id) => api.patch(`/tasks/${id}/archive`)));
                const failed = results.filter((r) => r.status === "rejected").length;
                setBusy(false);
                setSelected(new Set());
                if (failed > 0) alert(`Archived ${ids.length - failed}. ${failed} failed.`);
                load();
              }}
              className="btn-primary text-xs flex items-center gap-1.5 !py-1.5 !px-3"
            >
              {busy ? "Archiving…" : `Archive ${selected.size}`}
            </button>
            <button
              data-testid="bulk-unarchive-btn"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                const ids = Array.from(selected);
                await Promise.allSettled(ids.map((id) => api.patch(`/tasks/${id}/unarchive`)));
                setBusy(false);
                setSelected(new Set());
                load();
              }}
              className="btn-ghost text-xs !py-1.5 !px-3"
            >
              Unarchive
            </button>
            {selectedArchivedCount > 0 && (
              <button
                data-testid="bulk-delete-btn"
                disabled={busy}
                onClick={async () => {
                  const confirm1 = window.prompt(`Permanently delete ${selectedArchivedCount} archived task${selectedArchivedCount === 1 ? "" : "s"}?\n\nThis action cannot be undone.\n\nType DELETE to confirm.`);
                  if (confirm1 !== "DELETE") return;
                  setBusy(true);
                  const results = await Promise.allSettled(selectedArchivedIds.map((id) => api.delete(`/tasks/${id}`)));
                  const okCount = results.filter((r) => r.status === "fulfilled").length;
                  const failCount = results.filter((r) => r.status === "rejected").length;
                  setBusy(false);
                  setSelected(new Set());
                  alert(`Delete complete.\n\n✓ ${okCount} task${okCount === 1 ? "" : "s"} deleted${failCount ? `\n✗ ${failCount} failed` : ""}`);
                  load();
                }}
                className="text-xs flex items-center gap-1.5 !py-1.5 !px-3 rounded border border-[var(--err)] text-[var(--err)] hover:bg-[var(--err-bg)] transition"
                title="Permanently delete selected ARCHIVED tasks"
              >
                <Trash2 size={11} strokeWidth={1.5} /> Delete {selectedArchivedCount} permanently
              </button>
            )}
            <button
              data-testid="bulk-clear-selection"
              onClick={() => setSelected(new Set())}
              className="text-xs text-[var(--ink-2)] underline hover:text-[var(--ink)]"
            >
              Clear
            </button>
          </div>
        </div>
        );
      })()}

      {tasks.length === 0 || (visibleTasks.length === 0 && view !== "table") ? (
        <div className="card-flat p-16 text-center" data-testid="task-empty">
          <div className="text-4xl mb-3">📭</div>
          <div className="text-sm text-[var(--ink-2)]">No tasks match the current filters.</div>
        </div>
      ) : view === "table" ? (
        <div className="card-flat overflow-x-auto" data-testid="task-table">
          <table className="tbl">
            <thead>
              <tr>
                {isAdmin && (
                  <th className="w-8">
                    <input
                      type="checkbox"
                      data-testid="select-all-tasks"
                      checked={visibleTasks.length > 0 && visibleTasks.every((t) => selected.has(t.id))}
                      onChange={(e) => {
                        const next = new Set(selected);
                        if (e.target.checked) visibleTasks.forEach((t) => next.add(t.id));
                        else visibleTasks.forEach((t) => next.delete(t.id));
                        setSelected(next);
                      }}
                      className="accent-[var(--navy)] w-3.5 h-3.5 cursor-pointer"
                    />
                  </th>
                )}
                {H("name", "Task")}
                {visibleCols.has("client") && H("client", "Client")}
                {visibleCols.has("service_type") && H("service_type", "Service Type")}
                {visibleCols.has("task_type") && H("task_type", "Task Type")}
                {visibleCols.has("task_billing") && H("task_billing", "Billing")}
                {visibleCols.has("assigned_by") && H("assigned_by", "Assigned By")}
                {visibleCols.has("assignee") && H("assignee", "Assign To")}
                {visibleCols.has("start_date") && H("start_date", "Start")}
                {visibleCols.has("deadline") && H("deadline", "Deadline")}
                {visibleCols.has("completion_date") && H("completion_date", "Completion")}
                {visibleCols.has("priority") && H("priority", "Priority")}
                {visibleCols.has("status") && H("status", "Status")}
              </tr>
            </thead>
            <tbody>
              {visibleTasks.length === 0 && (
                <tr><td colSpan={20} className="text-center text-sm text-[var(--ink-2)] py-10" data-testid="task-no-match">No tasks match the current filters.</td></tr>
              )}
              {visibleTasks.map((t) => (
                <tr key={t.id} onClick={() => setActiveId(t.id)} data-testid={`task-row-${t.id}`} className={`${t.archived ? "opacity-60" : ""} ${selected.has(t.id) ? "!bg-[var(--gold-soft)]" : ""}`}>
                  {isAdmin && (
                    <td onClick={(e) => e.stopPropagation()} className="!py-0">
                      <input
                        type="checkbox"
                        data-testid={`select-task-${t.id}`}
                        checked={selected.has(t.id)}
                        onChange={(e) => {
                          const next = new Set(selected);
                          e.target.checked ? next.add(t.id) : next.delete(t.id);
                          setSelected(next);
                        }}
                        className="accent-[var(--navy)] w-3.5 h-3.5 cursor-pointer"
                      />
                    </td>
                  )}
                  <td className="font-medium">
                    {t.archived && <span className="status-pill mr-2 border-[var(--ink-2)] text-[var(--ink-2)] bg-[var(--bg)]" title="Archived">ARCHIVED</span>}
                    {t.name}
                    {t.recurrence_index && t.recurrence_total > 1 && (
                      <span className="status-pill ml-2 border-[var(--navy)] text-[var(--navy)] bg-[var(--navy-soft)] text-[10px]" title={`Part of ${t.recurrence_total} recurring tasks`}>
                        {t.recurrence_index}/{t.recurrence_total}
                      </span>
                    )}
                  </td>
                  {visibleCols.has("client") && <td className="text-[var(--ink-2)]" data-testid={`task-client-${t.id}`}>{clientMap[t.client_id] || (t.all_clients ? "All Clients" : "—")}</td>}
                  {visibleCols.has("service_type") && <td><span className="status-pill border-[var(--line)] text-[var(--ink-2)]">{t.service_type || "—"}</span></td>}
                  {visibleCols.has("task_type") && <td className="text-sm" data-testid={`task-type-${t.id}`}>{t.task_type || "—"}</td>}
                  {visibleCols.has("task_billing") && (
                    <td>
                      <span className={`status-pill ${t.task_billing === "additional" ? "border-[var(--gold)] text-[var(--warn)] bg-[var(--gold-soft)]" : t.task_billing === "goodwill" ? "border-[var(--navy)] text-[var(--navy)] bg-[var(--navy-soft)]" : "border-[var(--line)] text-[var(--ink-2)]"}`}>
                        {TASK_BILLING_LABEL[t.task_billing] || "—"}
                        {t.task_billing === "additional" && t.billing_amount != null ? ` · £${Number(t.billing_amount).toLocaleString()}` : ""}
                      </span>
                    </td>
                  )}
                  {visibleCols.has("assigned_by") && <td>{userMap[t.assigned_by_id] || "—"}</td>}
                  {visibleCols.has("assignee") && <td>{userMap[t.assignee_id] || "—"}</td>}
                  {visibleCols.has("start_date") && <td className="font-mono-data text-xs">{(t.start_date || "").slice(0, 10) || "—"}</td>}
                  {visibleCols.has("deadline") && <td className="font-mono-data text-xs">{(t.deadline || "").slice(0, 10) || "—"}</td>}
                  {visibleCols.has("completion_date") && <td className="font-mono-data text-xs">{(t.completion_date || "").slice(0, 10) || "—"}</td>}
                  {visibleCols.has("priority") && <td><span className={`priority-dot p-${t.priority}`} /><span className="capitalize text-sm">{t.priority}</span></td>}
                  {visibleCols.has("status") && <td><span className={`status-pill s-${t.status}`}>{STATUS_LABEL[t.status] || t.status}</span></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-testid="task-kanban">
          {KANBAN_COLS.map((s) => (
            <div key={s} className={`kanban-col k-${s}`}>
              <div className="overline mb-4">{STATUS_LABEL[s]} · {visibleTasks.filter((t) => t.status === s).length}</div>
              {visibleTasks.filter((t) => t.status === s).map((t) => (
                <div key={t.id} onClick={() => setActiveId(t.id)} className="kanban-card" data-testid={`kanban-card-${t.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-mono-data text-[10px] uppercase tracking-wider text-[var(--ink-2)]">{t.service_type}</div>
                    <span className={`status-pill !py-0 !text-[9px] ${t.task_billing === "additional" ? "border-[var(--gold)] text-[var(--warn)] bg-[var(--gold-soft)]" : t.task_billing === "goodwill" ? "border-[var(--navy)] text-[var(--navy)] bg-[var(--navy-soft)]" : "border-[var(--line)] text-[var(--ink-2)]"}`}>
                      {t.task_billing === "additional" && t.billing_amount != null ? `£${Number(t.billing_amount).toLocaleString()}` : (TASK_BILLING_LABEL[t.task_billing] || "—").split(" ")[0]}
                    </span>
                  </div>
                  <div className="text-sm font-medium leading-snug mb-2">{t.name}</div>
                  <div className="text-xs text-[var(--ink-2)] mb-3">{clientMap[t.client_id] || (t.all_clients ? "All Clients" : "")}</div>
                  <div className="flex justify-between items-center text-[11px]">
                    <span className="font-mono-data">{(t.deadline || "").slice(0, 10)}</span>
                    <span className="capitalize"><span className={`priority-dot p-${t.priority}`} />{t.priority}</span>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {activeId && (
        <TaskDrawer taskId={activeId} clients={clients} users={users} onClose={() => { setActiveId(null); load(); }} />
      )}
      {showNew && (
        <NewTaskModal clients={clients} users={users} onClose={(saved) => { setShowNew(false); if (saved) load(); }} />
      )}
    </div>
  );
}

function NewTaskModal({ clients, users, onClose }) {
  const { user: me } = useAuth();
  const canReassignBy = me?.role === "admin" || me?.role === "manager";
  const [form, setForm] = useState({
    name: "",
    description: "",
    client_id: "",
    service_type: "Accounts",
    task_type: "",
    task_billing: "part_of_existing",
    billing_amount: "",
    assignee_id: "",
    assigned_by_id: me?.id || "",
    deadline: "",
    start_date: "",
    completion_date: "",
    priority: "medium",
    status: "not_started",
    recurrence: "",
    recurrence_end_date: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (form.task_billing === "additional") {
      const amt = parseFloat(form.billing_amount);
      if (!amt || amt <= 0) {
        setError("Billing amount (£) is required when Additional Billing is selected.");
        return;
      }
    }
    setSaving(true);
    const body = { ...form };
    if (!body.client_id) { setError("Please choose a client, or All Clients for an internal task."); setSaving(false); return; }
    if (body.client_id === "__all__") { delete body.client_id; body.all_clients = true; }
    if (!body.assignee_id) delete body.assignee_id;
    if (!body.assigned_by_id) delete body.assigned_by_id;
    if (!body.start_date) delete body.start_date;
    if (!body.deadline) delete body.deadline;
    if (!body.completion_date) delete body.completion_date;
    if (body.task_billing === "additional") {
      body.billing_amount = parseFloat(body.billing_amount);
    } else {
      delete body.billing_amount;
    }
    // Recurrence: only send when both fields present; require start_date OR deadline as anchor
    if (body.recurrence && body.recurrence_end_date) {
      if (!body.start_date && !body.deadline) {
        setError("Recurrence needs a Start Date (or Deadline) as anchor.");
        setSaving(false);
        return;
      }
    } else {
      delete body.recurrence;
      delete body.recurrence_end_date;
    }
    try {
      await api.post("/tasks", body);
      onClose(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to create task.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="backdrop" onClick={() => onClose(false)} />
      <div className="drawer" data-testid="new-task-modal">
        <form onSubmit={submit} className="p-8">
          <div className="overline mb-2">Create</div>
          <h2 className="font-display text-3xl mb-8">New task.</h2>

          <Field label="Name">
            <input data-testid="nt-name" required className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Description">
            <textarea data-testid="nt-desc" className="input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Client">
              <select data-testid="nt-client" required className="input" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
                <option value="" disabled>Select client…</option>
                <option value="__all__">All Clients (internal task)</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.client_name}</option>)}
              </select>
            </Field>
            <Field label="Service Type">
              <select data-testid="nt-service-type" className="input" value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })}>
                {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Task Type">
              <select data-testid="nt-task-type" className="input" value={form.task_type} onChange={(e) => setForm({ ...form, task_type: e.target.value })}>
                <option value="">—</option>
                {TASK_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label={canReassignBy ? "Assigned By" : "Assigned By (you)"}>
              <select
                data-testid="nt-assigned-by"
                className="input"
                value={form.assigned_by_id}
                onChange={(e) => setForm({ ...form, assigned_by_id: e.target.value })}
                disabled={!canReassignBy}
              >
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </Field>
            <Field label="Assign To">
              <select data-testid="nt-assignee" className="input" value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })}>
                <option value="">— Unassigned</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </Field>
            <Field label="Priority">
              <select data-testid="nt-priority" className="input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                {["low", "medium", "high", "urgent"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select data-testid="nt-status" className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            </Field>
            <Field label="Start Date">
              <input data-testid="nt-start" type="date" className="input" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </Field>
            <Field label="Deadline">
              <input data-testid="nt-deadline" type="date" className="input" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
            </Field>
            <Field label="Completion Date">
              <input data-testid="nt-completion" type="date" className="input" value={form.completion_date} onChange={(e) => setForm({ ...form, completion_date: e.target.value })} />
            </Field>
          </div>

          {/* Recurrence — generates a series of tasks between start and end date */}
          <div className="card-flat p-4 mb-4 border-l-4 border-l-[var(--navy)]" data-testid="nt-recurrence-panel">
            <div className="overline mb-2">Recurrence · optional</div>
            <div className="text-xs text-[var(--ink-2)] mb-3 leading-relaxed">
              Generate a series of tasks automatically. Uses <span className="font-mono-data">Start Date</span> (or <span className="font-mono-data">Deadline</span>) above as the first occurrence.
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Repeats">
                <select
                  data-testid="nt-recurrence"
                  className="input"
                  value={form.recurrence}
                  onChange={(e) => setForm({ ...form, recurrence: e.target.value })}
                >
                  <option value="">None (single task)</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </Field>
              <Field label="Recurrence End Date">
                <input
                  data-testid="nt-recurrence-end"
                  type="date"
                  className="input"
                  value={form.recurrence_end_date}
                  onChange={(e) => setForm({ ...form, recurrence_end_date: e.target.value })}
                  disabled={!form.recurrence}
                />
              </Field>
            </div>
            {form.recurrence && form.recurrence_end_date && (form.start_date || form.deadline) && (
              <div className="text-xs text-[var(--ink-2)] mt-2 font-mono-data">
                Will generate <span className="text-[var(--navy)] font-medium">{estimateOccurrences(form.start_date || form.deadline, form.recurrence_end_date, form.recurrence)}</span> tasks.
              </div>
            )}
          </div>

          <Field label="Task Billing">
            <div className="flex flex-col gap-2" data-testid="nt-task-billing-group">
              {TASK_BILLING.map((b) => (
                <label key={b.key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="task_billing"
                    value={b.key}
                    data-testid={`nt-billing-${b.key}`}
                    checked={form.task_billing === b.key}
                    onChange={(e) => setForm({ ...form, task_billing: e.target.value })}
                  />
                  {b.label}
                </label>
              ))}
            </div>
          </Field>

          {form.task_billing === "additional" && (
            <Field label="Billing Amount (£)">
              <input
                data-testid="nt-billing-amount"
                type="number"
                min="0"
                step="0.01"
                required
                className="input"
                value={form.billing_amount}
                onChange={(e) => setForm({ ...form, billing_amount: e.target.value })}
                placeholder="e.g. 350.00"
              />
            </Field>
          )}

          {error && <div className="text-sm text-[var(--err)] mb-3" data-testid="nt-error">{error}</div>}

          <div className="flex gap-2 mt-8">
            <button data-testid="nt-submit" disabled={saving} className="btn-primary">{saving ? "Saving…" : "Create task"}</button>
            <button type="button" onClick={() => onClose(false)} className="btn-ghost">Cancel</button>
          </div>
        </form>
      </div>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-4">
      <label className="label-overline">{label}</label>
      {children}
    </div>
  );
}


// Preview: how many tasks the backend will generate for a recurrence config.
// Mirrors _expand_recurrence() in /app/backend/routers/tasks.py.
function estimateOccurrences(anchorISO, endISO, unit) {
  if (!anchorISO || !endISO || !unit) return 0;
  const a = new Date(anchorISO);
  const e = new Date(endISO);
  if (isNaN(a) || isNaN(e) || e < a) return 0;
  let count = 0;
  const cur = new Date(a);
  let safety = 0;
  while (cur <= e && safety < 500) {
    count += 1;
    if (unit === "daily") cur.setDate(cur.getDate() + 1);
    else if (unit === "weekly") cur.setDate(cur.getDate() + 7);
    else if (unit === "monthly") cur.setMonth(cur.getMonth() + 1);
    else if (unit === "quarterly") cur.setMonth(cur.getMonth() + 3);
    else if (unit === "yearly") cur.setFullYear(cur.getFullYear() + 1);
    else break;
    safety += 1;
  }
  return count;
}
